import {
  getStoredRevision,
  REVISION_HEADERS,
  setStoredRevision,
} from './revisionsKeys';
import { type FetchOptions, ApiError, REVISION_TYPES } from './types';

const API_BASE_URL = import.meta.env.VITE_PATH_PROXY || '';

const createHeaders = (
  groupId: string,
  method?: string,
  body?: unknown
): Record<string, string> => {
  const headers: Record<string, string> = {};

  // No Content-Type on GET: keeps cross-origin GETs "simple" (no CORS preflight)
  if (method !== 'GET' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  REVISION_TYPES.forEach((revisionType) => {
    const storedRevision = getStoredRevision(groupId, revisionType);

    if (storedRevision) {
      headers[REVISION_HEADERS[revisionType]] = storedRevision;
    }
  });

  return headers;
};

const prepareBody = (body: unknown): BodyInit | null => {
  if (body === null || body === undefined) {
    return null;
  }
  if (body instanceof FormData) {
    return body;
  }
  if (typeof body === 'string') {
    return body;
  }

  return JSON.stringify(body);
};

export const createApiClient = (baseURL: string) => {
  const send = async (
    endpoint: string,
    options: FetchOptions
  ): Promise<Response> => {
    const { groupId, ...fetchOptions } = options;
    const url = `${baseURL}${endpoint}`;

    const headers = {
      ...createHeaders(groupId, fetchOptions.method, fetchOptions.body),
      ...fetchOptions.headers,
    };

    const response = await fetch(url, {
      credentials: 'include',
      ...fetchOptions,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      throw new ApiError(
        errorData.error ||
          errorData.message ||
          errorData.reason ||
          `Error ${response.status}`,
        response.status,
        response,
        errorData
      );
    }

    // Only successful writes refresh the stored revisions; a response only carries
    // the headers of the streams it touched, so a missing header keeps the stored one
    if (fetchOptions.method !== 'GET') {
      REVISION_TYPES.forEach((revisionType) => {
        const revision = response.headers.get(REVISION_HEADERS[revisionType]);

        if (revision !== null) {
          setStoredRevision(groupId, revision, revisionType);
        }
      });
    }

    return response;
  };

  const requestBlob = async (
    endpoint: string,
    options: FetchOptions
  ): Promise<Blob> => {
    const response = await send(endpoint, options);

    return response.blob();
  };

  const execute = async <T>(
    endpoint: string,
    options: FetchOptions
  ): Promise<T> => {
    const response = await send(endpoint, options);

    if (response.status === 204) {
      return {} as T;
    }

    if (response.headers.get('content-length') === '0') {
      return {} as T;
    }

    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      const text = await response.text();

      if (!text || text.trim() === '') {
        return {} as T;
      }

      try {
        return JSON.parse(text) as T;
      } catch (error) {
        console.error('[API] JSON parse error:', error);

        return {} as T;
      }
    }

    return {} as T;
  };

  return {
    get: <T>(endpoint: string, options: FetchOptions) =>
      execute<T>(endpoint, { ...options, method: 'GET' }),
    post: <T>(endpoint: string, body: unknown, options: FetchOptions) =>
      execute<T>(endpoint, {
        ...options,
        method: 'POST',
        body: prepareBody(body),
      }),
    put: <T>(endpoint: string, body: unknown, options: FetchOptions) =>
      execute<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: prepareBody(body),
      }),
    patch: <T>(endpoint: string, body: unknown, options: FetchOptions) =>
      execute<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: prepareBody(body),
      }),
    delete: <T>(endpoint: string, options: FetchOptions) =>
      execute<T>(endpoint, { ...options, method: 'DELETE' }),
    // Raw response for existence/permission probes that carry no body
    options: (endpoint: string, options: FetchOptions) =>
      send(endpoint, { ...options, method: 'OPTIONS' }),
    getBlob: (endpoint: string, options: FetchOptions) =>
      requestBlob(endpoint, { ...options, method: 'GET' }),
    postBlob: (endpoint: string, body: unknown, options: FetchOptions) =>
      requestBlob(endpoint, {
        ...options,
        method: 'POST',
        body: prepareBody(body),
      }),
  };
};

export const apiClient = createApiClient(API_BASE_URL);
